/**
 * Maintenance Sub-section (Control Centre)
 * Outstanding defects, defects reported per day, and VOR (Aquarius). Samsara
 * fault codes are not shown (Darcica, 21 Oct 2026: defects matter, fault codes
 * much less); they are still ingested and Zoe can answer about them.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Wrench,
  RefreshCw,
  AlertTriangle,
  CarFront,
  ChevronRight,
} from 'lucide-react';
import type { TimePeriod } from '../TimePeriodSelector';
import ControlCentreKPICard from './ControlCentreKPICard';
import ControlCentrePeriodPanel, { coerceControlCentrePeriod } from './ControlCentrePeriodPanel';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import BarChart from '../BarChart';
import { UNKNOWN_CELL } from '../../utils/uiText';
import { isOpenMaintenanceDefect } from '../../utils/maintenanceDefects';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import ControlCentreDailyChecksPdfExport from './ControlCentreDailyChecksPdfExport';
import DailySafetyBriefPanel, { type DailySafetyBriefNavigate } from './DailySafetyBriefPanel';
import DataTable from '../DataTable';
import MaintenanceScheduleView from './MaintenanceScheduleView';
import { API_ENDPOINTS } from '../../config/api';
import { isPlaceholderOutstandingDefectRow } from '../../utils/defectRules';
import { authenticatedFetch } from '../../utils/apiClient';
import { daysBetweenYmd, ukTodayYmd } from '../../utils/ukTime';
import { maintenancePeriodRange } from '../../utils/maintenancePeriodRange';
import { buildMaintenanceDefectChartDays } from '../../utils/maintenanceDefectChartDays';
import {
  STORED_VIEW_PREPARING_MESSAGE,
  STORED_VIEW_STALE_RECHECK_SECONDS,
  preparingRetrySeconds,
  storedViewStaleMessage,
  type StoredViewProjection,
} from '../../utils/storedView';
import {
  MAINTENANCE_SCHEDULE_HISTORY_DAYS,
} from '../../utils/maintenanceScheduleWindows';
import { useTab } from '../../contexts/TabContext';
import { useVisibilityTruthRefresh } from '../../hooks/useVisibilityTruthRefresh';
import { formatDataCurrentAs } from '../../utils/ordersDailyPresentation';
import { defectStatusRowAccent, formatDefectStatusLabel } from '../../utils/defectStatus';

/**
 * Health answers "what is wrong right now" (faults, defects, VOR).
 * Schedule answers "what is coming up and what have we missed".
 *
 * These are different jobs done at different times of day, and this page was
 * already carrying three KPIs, two charts and three tables — growing it further
 * would bury both.
 */
type MaintenanceView = 'health' | 'schedule';

/**
 * Health re-read cooldown on browser alt-tab only. Returning to Control Centre
 * always refreshes (see useVisibilityTruthRefresh hub-return path).
 */
const HEALTH_TRUTH_REFRESH_MIN_INTERVAL_MS = 60_000;
/**
 * Schedule hits a heavier 90-day lookback — only auto-refresh when that
 * sub-view is open; alt-tab cooldown is longer than Health.
 */
const SCHEDULE_TRUTH_REFRESH_MIN_INTERVAL_MS = 5 * 60_000;

export interface MaintenanceSummaryData {
  /** Present when served from the stored Maintenance tab view. */
  projection?: StoredViewProjection;
  /** Oldest successful fetch among the feeds supplying the Maintenance view, ISO UTC. */
  data_current_as_of?: string | null;
  fleet_vehicle_count: number;
  /** Defects reported per day (by reported date), split by latest status; null when it could not be read. */
  defects_by_day: Array<{ day: string; not_fixed: number; in_progress: number; open: number; fixed: number }> | null;
  /** The defects behind each bar, latest status. */
  defects_by_day_detail: Array<{ day: string; vehicle: string; details: string; status: string; portal_id: string; fixed: boolean }> | null;
  open_defect_count: number;
  by_severity: Record<string, number>;
  by_status?: Record<string, number>;
  defects: Array<{
    vehicle: string;
    details: string;
    severity: string;
    reported_date: string;
    category?: string;
    date?: string;
    status?: string;
    portal_id?: string;
    resolved?: boolean;
    vor_state?: boolean;
    card_id?: string;
    days_open?: number | null;
    latest_audit?: {
      datetime?: string;
      user?: string;
      type?: string;
      comment?: string;
    } | null;
    /** Audit trail, oldest first (at most 10 entries). */
    audit_history?: Array<{ datetime?: string; user?: string; type?: string; comment?: string }>;
  }>;
  check_compliance_pct: number;
  checks_done: number;
  checks_expected: number;
  vehicles_checked: number;
  failed_checks: Array<{
    vehicle: string;
    driver_name: string;
    check_result: string;
    check_date: string;
    status?: string;
  }>;
  vor_asset_count: number;
  vor_assets: Array<{
    vehicle: string;
    reason: string;
    vor_category: string;
    vor_days: number | null;
    vor_start: string;
    date_reported: string;
  }>;
}

/** When a defect last changed: its latest audit entry, else when it was reported. */
function defectLastUpdated(d: DefectRow): string {
  return d.latest_audit?.datetime || d.reported_date || d.date || '';
}

function formatAuditWhen(value: string | undefined): string {
  if (!value) return '';
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** One audit line: "Fixed by rhys.darcica, 20 Oct 10:01: will inspect". */
function auditLine(a: { datetime?: string; user?: string; type?: string; comment?: string }): string {
  const who = a.user ? ` by ${a.user}` : '';
  const when = a.datetime ? `, ${formatAuditWhen(a.datetime)}` : '';
  const note = a.comment?.trim() ? `: ${a.comment.trim()}` : '';
  return `${a.type || 'Update'}${who}${when}${note}`;
}

/** A vehicle's defects in the Defect status table, most recently updated first. */
function VehicleDefectsDetail({ defects }: { defects: DefectRow[] }) {
  const [historyOpen, setHistoryOpen] = useState<Set<string>>(new Set());
  return (
    <ul className="divide-y divide-border/60 rounded-lg border border-destructive/40 bg-destructive/10 px-3">
      {defects.map((d, i) => {
        const key = d.portal_id || `${i}`;
        const statusRaw = d.status || (d.resolved ? 'Fixed' : 'Notfixed');
        const statusAccent = defectStatusRowAccent(statusRaw, d.resolved);
        const statusBadgeClass = statusAccent === 'red'
          ? 'border-destructive/60 bg-destructive/40 text-destructive'
          : statusAccent === 'amber'
            ? 'border-warning/60 bg-warning/40 text-warning'
            : statusAccent === 'green'
              ? 'border-success/60 bg-success/40 text-success'
              : 'border-border bg-card text-foreground';
        const history = d.audit_history ?? [];
        const showHistory = historyOpen.has(key);
        return (
          <li key={key} className="py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded border ${statusBadgeClass}`}>
                {formatDefectStatusLabel(statusRaw, d.resolved)}
              </span>
              {d.vor_state ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded border border-destructive/60 bg-destructive/40 text-destructive">VOR</span>
              ) : null}
              <span className="text-xs text-muted-foreground">
                Reported {formatAuditWhen(d.reported_date || d.date)}
                {d.portal_id ? ` · ${d.portal_id}` : ''}
              </span>
            </div>
            <p className="mt-1 text-sm text-foreground whitespace-normal break-words">{cleanDefectDetails(d.details) || UNKNOWN_CELL}</p>
            {d.latest_audit ? <p className="mt-1 text-xs text-muted-foreground">{auditLine(d.latest_audit)}</p> : null}
            {history.length > 1 ? (
              <>
                <button
                  type="button"
                  className="mt-1 text-xs text-ring hover:text-ring"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHistoryOpen((prev) => {
                      const next = new Set(prev);
                      if (next.has(key)) next.delete(key); else next.add(key);
                      return next;
                    });
                  }}
                >
                  {showHistory ? 'Hide history' : `Show history (${history.length})`}
                </button>
                {showHistory ? (
                  <ol className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {[...history].reverse().map((a, j) => (
                      <li key={j}>{auditLine(a)}</li>
                    ))}
                  </ol>
                ) : null}
              </>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Defects reported per day: lifecycle status colors (match Defect status). */
const DEFECT_NOT_FIXED_COLOR = '#f87171';
const DEFECT_IN_PROGRESS_COLOR = '#fbbf24';
const DEFECT_FIXED_COLOR = '#22c55e';

/** The defects behind one bar of Defects reported per day. */
const DEFECTS_REPORTED_COLUMNS: ColumnDef[] = [
  { key: 'vehicle', header: 'Vehicle' },
  { key: 'details', header: 'Defect', render: (v) => cleanDefectDetails(String(v ?? '')) },
  { key: 'status', header: 'Status' },
  { key: 'portal_id', header: 'Ref' },
];

const DEFECT_COLUMNS: ColumnDef[] = [
  { key: 'vehicle', header: 'Vehicle' },
  {
    key: 'defect_count',
    header: 'Defects',
    align: 'right',
    sortValue: (row) => Number(row.defect_count ?? 1),
    render: (v) => (v != null && v !== '' ? String(v) : '1'),
  },
  { key: 'details', header: 'Details' },
  {
    key: 'reported_date',
    header: 'Last updated',
    sortValue: (row) => recencySortKey(String(row.reported_date || row.date || '')),
    render: (_v, row) => renderDaysAgo(String(row.reported_date || row.date || '')),
  },
  {
    key: 'vor_state',
    header: 'VOR',
    sortValue: (row) => (row.vor_state ? 1 : 0),
    render: (_v, row) => (row.vor_state ? 'Yes' : 'No'),
  },
];

type DefectRow = MaintenanceSummaryData['defects'][number];

type GroupedDefectRow = DefectRow & { defect_count: number; days_open?: number };

/** Strip internal Aquarius summary labelling from defect text. */
function cleanDefectDetails(raw: string | undefined | null): string {
  if (!raw) return '';
  return String(raw)
    .replace(/\s*\(Aquarius assets summary\)\s*/gi, '')
    .trim();
}

/**
 * Collapse multiple outstanding defects for the same registration into one row
 * so the table shows one line per vehicle with a defect count.
 */
function groupDefectsByVehicle(defects: DefectRow[]): GroupedDefectRow[] {
  const byReg = new Map<string, DefectRow[]>();
  for (const d of defects) {
    if (isPlaceholderOutstandingDefectRow(d)) continue;
    const key = String(d.vehicle || '').trim().toUpperCase() || '—';
    const list = byReg.get(key);
    if (list) list.push(d);
    else byReg.set(key, [d]);
  }

  const grouped: GroupedDefectRow[] = [];
  for (const [, rows] of byReg) {
    const count = rows.length;
    const primary = rows[0];

    let earliest = rows[0]?.reported_date || rows[0]?.date || '';
    let earliestMs = earliest ? Date.parse(earliest) : Number.POSITIVE_INFINITY;
    let anyVor = false;
    for (const r of rows) {
      if (r.vor_state) anyVor = true;
      const raw = r.reported_date || r.date || '';
      const ms = raw ? Date.parse(raw) : Number.NaN;
      if (!Number.isNaN(ms) && ms < earliestMs) {
        earliestMs = ms;
        earliest = raw;
      }
    }

    const cleaned = rows
      .map((r) => cleanDefectDetails(r.details))
      .filter(Boolean);
    const summaryOnly = cleaned.every((t) =>
      /^Outstanding defect \d+ of \d+$/i.test(t),
    );
    let details: string;
    if (summaryOnly && count > 0) {
      details = `${count} outstanding defect${count === 1 ? '' : 's'}`;
    } else {
      const unique = [...new Set(cleaned)];
      details =
        unique.length <= 2
          ? unique.join('; ') || `${count} outstanding defect${count === 1 ? '' : 's'}`
          : `${count} outstanding defects`;
    }

    const daysOpen =
      Number.isFinite(earliestMs) && earliestMs !== Number.POSITIVE_INFINITY
        ? Math.max(0, Math.floor((Date.now() - earliestMs) / 86_400_000))
        : undefined;

    grouped.push({
      vehicle: rows[0]?.vehicle || '—',
      details,
      severity: primary?.severity || 'unknown',
      reported_date: earliest,
      category: primary?.category,
      date: earliest,
      status: primary?.status,
      vor_state: anyVor,
      defect_count: count,
      days_open: daysOpen,
    });
  }

  grouped.sort((a, b) => {
    if (b.defect_count !== a.defect_count) return b.defect_count - a.defect_count;
    return (b.days_open ?? -1) - (a.days_open ?? -1);
  });

  return grouped;
}

/** "2026-07-16T00:00:00Z" → "2026-07-16" */
function formatDateYmd(value: string | null | undefined): string {
  if (!value) return '—';
  const m = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toISOString().slice(0, 10);
}

/** Days between date_reported and today → "X days ago" / "Today". */
function formatDaysAgo(value: string | null | undefined): string {
  if (!value) return '—';
  const days = daysAgoSortKey(value);
  if (days < 0) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function daysAgoSortKey(value: string | null | undefined): number {
  if (!value) return -1;
  // Same day the table shows (`formatDateYmd`): the date as written, against today in the UK.
  const reportedYmd = formatDateYmd(value);
  const days = daysBetweenYmd(reportedYmd, ukTodayYmd());
  return days == null ? -1 : Math.max(0, days);
}

/** Timestamp ms for Last Updated columns — higher = more recent so DataTable `desc` = Today first. */
function recencySortKey(value: string | null | undefined): number {
  if (!value) return 0;
  const ms = Date.parse(String(value));
  return Number.isNaN(ms) ? 0 : ms;
}

/** Most-recent-first copy of `rows` using a date extractor. */
function sortByRecency<T>(
  rows: readonly T[],
  getDate: (row: T) => string | null | undefined,
): T[] {
  return [...rows].sort(
    (a, b) => recencySortKey(getDate(b)) - recencySortKey(getDate(a)),
  );
}

/** green <7d · amber 7–20d · red ≥21d */
function daysAgoToneClass(days: number): string {
  if (days < 0) return 'text-muted-foreground';
  if (days < 7) return 'text-success';
  if (days < 21) return 'text-warning';
  return 'text-destructive';
}

function renderDaysAgo(value: string | null | undefined) {
  const days = daysAgoSortKey(value);
  const label = formatDaysAgo(value);
  if (label === '—') return <span className="text-muted-foreground">—</span>;
  return <span className={`font-medium ${daysAgoToneClass(days)}`}>{label}</span>;
}

const VOR_COLUMNS: ColumnDef[] = [
  { key: 'vehicle', header: 'Vehicle' },
  {
    key: 'vor_start',
    header: 'Start Date',
    render: (v) => formatDateYmd(v != null ? String(v) : undefined),
  },
  {
    key: 'vor_days',
    header: 'Days Off-Road',
    align: 'right',
    render: (v) => (v != null && v !== '' ? String(v) : '—'),
  },
  {
    key: 'date_reported',
    header: 'Last Updated',
    sortValue: (row) => recencySortKey(String(row.date_reported || '')),
    render: (v) => renderDaysAgo(v != null ? String(v) : undefined),
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (v) => (
      <span className="whitespace-normal break-words block max-w-xl">
        {v != null && v !== '' ? String(v) : '—'}
      </span>
    ),
  },
  {
    key: 'vor_category',
    header: 'Category',
    render: (v) => (
      <span className="whitespace-normal break-words block">
        {v != null && v !== '' ? String(v) : '—'}
      </span>
    ),
  },
];

interface MaintenanceSectionProps {
  onFetchData?: () => void | Promise<void>;
  isLoading?: boolean;
  highlightKPI?: string | null;
  onHighlightConsumed?: () => void;
  initialPeriod?: TimePeriod;
  useMockData?: boolean;
  /** Incremented by parent after Maintenance vendor ingest — forces Health + Schedule re-read. */
  refreshKey?: number;
}

const MOCK_SUMMARY: MaintenanceSummaryData = {
  fleet_vehicle_count: 48,
  defects_by_day: [
    { day: '2026-07-31', not_fixed: 1, in_progress: 0, open: 1, fixed: 1 },
    { day: '2026-08-01', not_fixed: 1, in_progress: 1, open: 2, fixed: 2 },
    { day: '2026-08-02', not_fixed: 1, in_progress: 0, open: 1, fixed: 0 },
    { day: '2026-08-03', not_fixed: 0, in_progress: 0, open: 0, fixed: 3 },
    { day: '2026-08-04', not_fixed: 2, in_progress: 1, open: 3, fixed: 2 },
    { day: '2026-08-05', not_fixed: 1, in_progress: 0, open: 1, fixed: 1 },
    { day: '2026-08-06', not_fixed: 1, in_progress: 1, open: 2, fixed: 1 },
  ],
  defects_by_day_detail: [
    { day: '2026-08-06', vehicle: 'KU22 WPJ', details: 'Body \u2014 Tail lift', status: 'Notfixed', portal_id: 'D1', fixed: false },
    { day: '2026-08-06', vehicle: 'SL22 CMO', details: 'Tyres', status: 'Notfixed', portal_id: 'D2', fixed: false },
    { day: '2026-08-06', vehicle: 'WV22 NKK', details: 'Lights', status: 'Fixed', portal_id: 'D3', fixed: true },
  ],
  open_defect_count: 9,
  by_severity: { critical: 3, major: 4, minor: 2 },
  defects: [
    {
      vehicle: 'FL-2041',
      details: 'Rear brake wear indicator triggered',
      severity: 'critical',
      reported_date: '2026-08-02T00:00:00Z',
    },
    {
      vehicle: 'FL-1988',
      details: 'Coolant leak — driver reported at walkaround',
      severity: 'critical',
      reported_date: '2026-08-04T00:00:00Z',
    },
  ],
  check_compliance_pct: 91,
  checks_done: 38,
  checks_expected: 42,
  vehicles_checked: 38,
  failed_checks: [
    { vehicle: 'FL-1955', driver_name: 'M. Okafor', check_result: 'fail', check_date: '2026-08-06T07:00:00Z' },
    { vehicle: 'FL-2063', driver_name: 'D. Whitfield', check_result: 'fail', check_date: '2026-08-06T07:10:00Z' },
  ],
  vor_asset_count: 2,
  vor_assets: [
    {
      vehicle: 'PN73KDV',
      reason: 'DPF / EXHAUST PROBLEMS',
      vor_category: 'Unexplained Damage',
      vor_days: 21,
      vor_start: '2026-07-16T00:00:00Z',
      date_reported: '2026-08-05T00:00:00Z',
    },
    {
      vehicle: 'SL22CMU',
      reason: 'Vehicle Breakdown — missed PMI AND MOT',
      vor_category: 'Unexplained Damage',
      vor_days: 34,
      vor_start: '2026-07-03T00:00:00Z',
      date_reported: '2026-08-05T00:00:00Z',
    },
  ],
};

function statusBreakdown(
  defects: Array<{ status?: string; resolved?: boolean }>,
): Array<{ label: string; count: number; pct: number; tone: string }> {
  const by: Record<string, number> = {};
  for (const d of defects) {
    const raw = String(d.status || '').trim();
    const key = raw || (d.resolved ? 'Fixed' : 'Notfixed');
    by[key] = (by[key] || 0) + 1;
  }
  const order = ['Notfixed', 'InProgress', 'NextPMI', 'Fixed'];
  const total = Object.values(by).reduce((s, n) => s + n, 0) || 1;
  const keys = [
    ...order.filter((k) => by[k] != null),
    ...Object.keys(by).filter((k) => !order.includes(k)),
  ];
  return keys.map((k) => {
    const count = Number(by[k] || 0);
    const lower = k.toLowerCase();
    return {
      label: formatDefectStatusLabel(k),
      count,
      pct: Math.round((count / total) * 100),
      tone:
        lower === 'notfixed'
          ? 'critical'
          : lower === 'inprogress' || lower === 'nextpmi'
            ? 'major'
            : 'minor',
    };
  });
}

export default function MaintenanceSection({
  onFetchData,
  isLoading = false,
  highlightKPI,
  onHighlightConsumed,
  initialPeriod,
  useMockData = false,
  refreshKey = 0,
}: MaintenanceSectionProps) {
  const { activeTab } = useTab();
  const [activeView, setActiveView] = useState<MaintenanceView>('health');
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    () => coerceControlCentrePeriod(initialPeriod, 'today'),
  );
  const [defectStatusFilter, setDefectStatusFilter] = useState<string>('open');
  /** When set, Active Fault Codes table shows vehicles that reported a DTC on this day (YYYY-MM-DD). */
  const [summary, setSummary] = useState<MaintenanceSummaryData | null>(useMockData ? MOCK_SUMMARY : null);
  const [summaryRangeKey, setSummaryRangeKey] = useState('');
  const [periodLoading, setPeriodLoading] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);
  /** The period's stored figures are being built (202): show that, and ask again. */
  const [periodPreparing, setPeriodPreparing] = useState(false);
  const summaryRecheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchSummaryRef = useRef<() => Promise<void>>(async () => {});
  const [kpiModal, setKpiModal] = useState<{
    open: boolean;
    title: string;
    subtitle?: string;
    columns: ColumnDef[];
    rows: Record<string, unknown>[];
    searchKeys?: string[];
  }>({ open: false, title: '', columns: [], rows: [] });
  const kpiGridRef = useRef<HTMLDivElement>(null);
  const rangeKeyRef = useRef<string>('');
  /** Cancels stale Health responses when refreshKey / period / visibility overlap. */
  const healthRequestSeq = useRef(0);

  const healthTruth = useVisibilityTruthRefresh({
    enabled: !useMockData && activeView === 'health',
    activeTab,
    minIntervalMs: HEALTH_TRUTH_REFRESH_MIN_INTERVAL_MS,
  });
  const scheduleTruth = useVisibilityTruthRefresh({
    enabled: !useMockData && activeView === 'schedule',
    activeTab,
    minIntervalMs: SCHEDULE_TRUTH_REFRESH_MIN_INTERVAL_MS,
  });
  const healthMarkRef = useRef(healthTruth.markTruthFetch);
  const scheduleMarkRef = useRef(scheduleTruth.markTruthFetch);
  const scheduleBumpRef = useRef(scheduleTruth.bump);
  healthMarkRef.current = healthTruth.markTruthFetch;
  scheduleMarkRef.current = scheduleTruth.markTruthFetch;
  scheduleBumpRef.current = scheduleTruth.bump;

  // Parent ingest bump — stamp both cooldowns and force Schedule re-read.
  // Health already depends on refreshKey in its fetch effect.
  useEffect(() => {
    if (refreshKey <= 0) return;
    healthMarkRef.current();
    scheduleMarkRef.current();
    scheduleBumpRef.current({ force: true });
  }, [refreshKey]);

  useEffect(() => {
    if (initialPeriod != null) {
      setSelectedPeriod(coerceControlCentrePeriod(initialPeriod, 'today'));
    }
  }, [initialPeriod]);

  const getDateRange = useMemo(() => maintenancePeriodRange(selectedPeriod), [selectedPeriod]);

  const fetchSummary = useCallback(async () => {
    if (useMockData) {
      setSummary(MOCK_SUMMARY);
      return;
    }
    const seq = ++healthRequestSeq.current;
    if (summaryRecheckTimer.current) clearTimeout(summaryRecheckTimer.current);
    summaryRecheckTimer.current = null;
    const from = getDateRange.startDate.toISOString();
    const to = getDateRange.endDate.toISOString();
    const key = `${from}|${to}`;
    rangeKeyRef.current = key;
    healthTruth.markTruthFetch();
    setPeriodLoading(true);
    setPeriodError(null);
    try {
      const res = await authenticatedFetch(
        API_ENDPOINTS.fleetIntelligence.maintenanceSummary({ from, to }),
      );
      if (seq !== healthRequestSeq.current) return;
      // Preset periods are stored views: 202 = the first copy is being built.
      // Ask again when it should be ready; nothing is computed per request.
      if (res.status === 202) {
        const wait = await preparingRetrySeconds(res);
        if (seq !== healthRequestSeq.current) return;
        setPeriodPreparing(true);
        summaryRecheckTimer.current = setTimeout(() => { void fetchSummaryRef.current(); }, wait * 1000);
        return;
      }
      setPeriodPreparing(false);
      if (!res.ok) {
        throw new Error(`Maintenance summary failed (${res.status})`);
      }
      const data = (await res.json()) as MaintenanceSummaryData;
      if (seq !== healthRequestSeq.current) return;
      if (rangeKeyRef.current === key) {
        setSummary(data);
        setSummaryRangeKey(key);
      }
      // Stale stored figures: shown with a note; look again once the requested refresh has landed.
      if (data.projection?.stale_reason) {
        summaryRecheckTimer.current = setTimeout(
          () => { void fetchSummaryRef.current(); },
          STORED_VIEW_STALE_RECHECK_SECONDS * 1000,
        );
      }
    } catch (err) {
      if (seq !== healthRequestSeq.current) return;
      setPeriodPreparing(false);
      console.error('[MaintenanceSection] summary fetch failed:', err);
      if (rangeKeyRef.current === key) {
        setPeriodError(err instanceof Error ? err.message : 'Failed to load maintenance data');
      }
    } finally {
      if (seq === healthRequestSeq.current && rangeKeyRef.current === key) {
        setPeriodLoading(false);
      }
    }
  }, [useMockData, getDateRange, healthTruth.markTruthFetch]);
  fetchSummaryRef.current = fetchSummary;

  useEffect(() => () => {
    if (summaryRecheckTimer.current) clearTimeout(summaryRecheckTimer.current);
  }, []);

  // Leaving demo mode must never leave the mock figures on screen: clear them so
  // the page shows loading, then the real summary or its error.
  useEffect(() => {
    setSummary(useMockData ? MOCK_SUMMARY : null);
    setSummaryRangeKey('');
  }, [useMockData]);

  // Only the Health view reads this summary, and it is an expensive query.
  // Changing the period while Schedule is open would otherwise fire it for
  // nothing. Switching back re-runs the effect, so the range stays current.
  useEffect(() => {
    if (activeView !== 'health') return;
    fetchSummary();
  }, [activeView, fetchSummary, refreshKey, healthTruth.refreshKey]);

  const showLoading = isLoading || periodLoading;
  const data = summary;
  const selectedRangeKey = `${getDateRange.startDate.toISOString()}|${getDateRange.endDate.toISOString()}`;
  const summaryMatchesSelectedPeriod = summaryRangeKey === selectedRangeKey;
  const dataCurrentAs = formatDataCurrentAs(summary?.data_current_as_of ?? null);

  const periodDefects = useMemo(() => {
    if (!data) return [];
    // Trust maintenance/summary window (occurred_at). Do not re-filter by reported_date —
    // that drops older open defects updated in-period and desyncs the brief count.
    return (data.defects || []).filter((d) => !isPlaceholderOutstandingDefectRow(d));
  }, [data]);

  const statusFilteredDefects = useMemo(() => {
    let list: typeof periodDefects;
    if (defectStatusFilter === 'all') list = periodDefects;
    else if (defectStatusFilter === 'open') list = periodDefects.filter(isOpenMaintenanceDefect);
    else {
      list = periodDefects.filter(
        (d) => String(d.status || '').toLowerCase() === defectStatusFilter.toLowerCase(),
      );
    }
    return sortByRecency(
      list,
      (d) => d.reported_date || d.date || d.latest_audit?.datetime || '',
    );
  }, [periodDefects, defectStatusFilter]);

  const openDefects = useMemo(
    () => periodDefects.filter(isOpenMaintenanceDefect),
    [periodDefects],
  );

  const groupedDefects = useMemo(
    () => groupDefectsByVehicle(openDefects),
    [openDefects],
  );

  const isSingleDayPeriod =
    selectedPeriod === 'today' ||
    selectedPeriod === 'yesterday' ||
    getDateRange.endDate.getTime() - getDateRange.startDate.getTime() < 26 * 60 * 60 * 1000;

  // Defects reported per day (Aquarius), split by latest lifecycle status. null = could not be read.
  const defectsByDayUnavailable = !!data && data.defects_by_day == null;
  const defectChartDays = useMemo(() => {
    return buildMaintenanceDefectChartDays(data?.defects_by_day || [], {
      now: new Date(),
      selectedStartKey: getDateRange.startDate.toISOString().split('T')[0],
      selectedEndKey: getDateRange.endDate.toISOString().split('T')[0],
      highlightSelectedDay: isSingleDayPeriod,
    });
  }, [data, getDateRange, isSingleDayPeriod]);


  /** A bar's defects: every defect reported that day, with its latest status. */
  const openDefectDay = useCallback((day: string) => {
    const rows = (summary?.defects_by_day_detail || [])
      .filter((d) => d.day === day)
      .map((d) => ({
        vehicle: d.vehicle,
        details: d.details,
        status: d.fixed ? 'Fixed' : formatDefectStatusLabel(d.status || 'Notfixed'),
        portal_id: d.portal_id,
      }));
    const label = new Date(`${day}T12:00:00.000Z`).toLocaleDateString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
    });
    const dayCount = summary?.defects_by_day?.find((d) => d.day === day);
    const reported = dayCount ? dayCount.open + dayCount.fixed : rows.length;
    const fixed = dayCount?.fixed ?? rows.filter((r) => r.status === 'Fixed').length;
    const detailLimitNote = rows.length < reported
      ? ` · showing ${rows.length} of ${reported} details`
      : '';
    setKpiModal({
      open: true,
      title: `Defects reported ${label}`,
      subtitle: `${reported} reported · ${fixed} fixed · ${reported - fixed} still open${detailLimitNote}`,
      columns: DEFECTS_REPORTED_COLUMNS,
      rows,
      searchKeys: ['vehicle', 'details', 'status', 'portal_id'],
    });
  }, [summary]);

  const statusRows = useMemo(() => statusBreakdown(periodDefects), [periodDefects]);
  const notFixedDefectCount = periodDefects.filter(
    (d) => !d.resolved && String(d.status || '').toLowerCase() !== 'fixed'
      && String(d.status || '').toLowerCase() !== 'inprogress',
  ).length;
  const inProgressDefectCount = periodDefects.filter(
    (d) => String(d.status || '').toLowerCase() === 'inprogress',
  ).length;

  const openDefectsModal = useCallback(() => {
    const vehicleCount = groupedDefects.length;
    const defectCount = openDefects.length;
    const rows = sortByRecency(groupedDefects, (d) => d.reported_date || d.date || '');
    setKpiModal({
      open: true,
      title: 'Outstanding Defects',
      subtitle: `${defectCount} defect${defectCount === 1 ? '' : 's'} across ${vehicleCount} vehicle${vehicleCount === 1 ? '' : 's'}`,
      columns: DEFECT_COLUMNS,
      rows,
      searchKeys: ['vehicle', 'details', 'reported_date', 'category', 'status'],
    });
  }, [groupedDefects, openDefects.length]);

  useEffect(() => {
    if (!highlightKPI || !summary) return;
    const timer = setTimeout(() => {
      const map: Record<string, () => void> = {
        'open-defects': () => openDefectsModal(),
        vor: () =>
          setKpiModal({
            open: true,
            title: 'Vehicles on VOR',
            subtitle: `${summary.vor_asset_count} assets`,
            columns: VOR_COLUMNS,
            rows: sortByRecency(summary.vor_assets || [], (v) => v.date_reported),
            searchKeys: ['vehicle', 'vor_start', 'reason', 'vor_category'],
          }),
      };
      map[highlightKPI]?.();
      onHighlightConsumed?.();
    }, 50);
    return () => clearTimeout(timer);
  }, [highlightKPI, summary, onHighlightConsumed, openDefectsModal]);

  const handleRefresh = async () => {
    if (onFetchData) await onFetchData();
    await fetchSummary();
  };

  const briefRange = useMemo(() => {
    const from = getDateRange.startDate.toISOString();
    const to = getDateRange.endDate.toISOString();
    return { from, to };
  }, [getDateRange]);

  const handleBriefNavigate = useCallback((target: DailySafetyBriefNavigate) => {
    if (target === 'maintenance_schedule') {
      setActiveView('schedule');
      return;
    }
    if (target === 'maintenance_vor') {
      setKpiModal({
        open: true,
        title: 'Vehicles on VOR',
        subtitle: `${summary?.vor_asset_count ?? 0} assets`,
        columns: VOR_COLUMNS,
        rows: sortByRecency(summary?.vor_assets || [], (v) => v.date_reported),
        searchKeys: ['vehicle', 'vor_start', 'reason', 'vor_category'],
      });
      requestAnimationFrame(() => {
        kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return;
    }
    if (target === 'maintenance_defects') {
      openDefectsModal();
      requestAnimationFrame(() => {
        kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [summary, openDefectsModal]);

  // Defect status table: one row per vehicle (the status filter applies first),
  // newest update first; a row expands to that vehicle's defects, newest first.
  const [expandedDefectVehicle, setExpandedDefectVehicle] = useState<string | null>(null);
  const defectVehicleGroups = useMemo(() => {
    const byReg = new Map<string, DefectRow[]>();
    for (const d of statusFilteredDefects) {
      const reg = String(d.vehicle || '').trim().toUpperCase() || UNKNOWN_CELL;
      byReg.set(reg, [...(byReg.get(reg) ?? []), d]);
    }
    return [...byReg.entries()]
      .map(([reg, list]) => {
        const defects = [...list].sort((x, y) => recencySortKey(defectLastUpdated(y)) - recencySortKey(defectLastUpdated(x)));
        return { reg, defects, lastUpdated: defectLastUpdated(defects[0]!) };
      })
      .sort((x, y) => recencySortKey(y.lastUpdated) - recencySortKey(x.lastUpdated));
  }, [statusFilteredDefects]);

  const defectTableRows = useMemo(
    () =>
      defectVehicleGroups.map((g) => {
        const counts = new Map<string, number>();
        for (const d of g.defects) {
          const label = formatDefectStatusLabel(d.status || (d.resolved ? 'Fixed' : 'Notfixed'), d.resolved);
          counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        const statusOrder = ['Not Fixed', 'In Progress', 'Fixed'];
        const anyVor = g.defects.some((d) => d.vor_state);
        // The vehicle's most urgent defect colours the row: any Not Fixed is red,
        // else any In Progress amber, else (all fixed) green.
        const accents = g.defects.map((d) => defectStatusRowAccent(d.status, d.resolved));
        const accent = accents.includes('red') ? 'red' : accents.includes('amber') ? 'amber'
          : accents.length > 0 && accents.every((a) => a === 'green') ? 'green' : undefined;
        const isOpen = expandedDefectVehicle === g.reg;
        return {
          id: g.reg,
          vehicle: (
            <span className="inline-flex items-center gap-1" title={isOpen ? 'Click to hide details' : 'Click to see details'}>
              <ChevronRight className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} aria-hidden />
              {g.reg}
            </span>
          ),
          vehicle_sortKey: g.reg,
          defect_count: g.defects.length,
          status: statusOrder
            .filter((label) => counts.has(label))
            .map((label) => `${counts.get(label)} ${label}`)
            .join(' · '),
          latest_audit: renderDaysAgo(g.lastUpdated),
          latest_audit_sortKey: recencySortKey(g.lastUpdated),
          vor: anyVor ? 'Yes' : 'No',
          vor_sortKey: anyVor ? 1 : 0,
          _group: g,
          ...(accent ? { _rowAccent: accent } : {}),
        };
      }),
    [defectVehicleGroups, expandedDefectVehicle],
  );

  const vorTableRows = useMemo(
    () =>
      sortByRecency(summary?.vor_assets || [], (v) => v.date_reported).map((v, i) => ({
        id: `${v.vehicle}-${i}`,
        vehicle: v.vehicle || '—',
        vor_start: formatDateYmd(v.vor_start),
        vor_start_sortKey: v.vor_start ? Date.parse(v.vor_start) || 0 : 0,
        vor_days: v.vor_days != null ? v.vor_days : '—',
        vor_days_sortKey: v.vor_days != null ? Number(v.vor_days) : -1,
        last_update: renderDaysAgo(v.date_reported),
        last_update_sortKey: recencySortKey(v.date_reported),
        reason: v.reason || '—',
        vor_category: v.vor_category || '—',
      })),
    [summary?.vor_assets],
  );

  /**
   * Header and view switch are shared by every state below. The Schedule view
   * has its own data source, so it must stay reachable when the health summary
   * is empty or still loading — otherwise an org with no Samsara faults can
   * never see its overdue MOTs.
   */
  const headerBlock = (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-3">
            <Wrench className="w-7 h-7 text-warning" />
            Maintenance
          </h3>
          <p className="text-muted-foreground mt-1">
            {activeView === 'health'
              ? 'Defects & VOR'
              : 'Upcoming, overdue & completed servicing'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {activeView === 'health' && <ControlCentreSourceBadge label="Samsara" />}
          <ControlCentreSourceBadge label="Aquarius" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        {activeView === 'health' && (
          <div className="flex flex-col items-start gap-1 min-w-0">
            <ControlCentrePeriodPanel
              value={selectedPeriod}
              onChange={setSelectedPeriod}
              accent="amber"
              ariaLabel="Maintenance time period"
            />
            {!useMockData && !periodLoading && !periodError && summaryMatchesSelectedPeriod
              && dataCurrentAs && (
              <div className="text-sm text-foreground min-w-0">
                Data current as of {dataCurrentAs}
              </div>
            )}
          </div>
        )}
        <div className="inline-flex rounded-lg border border-border bg-card/60 p-1 shrink-0 ml-auto">
          {([
            { id: 'health', label: 'Health', hint: 'What is wrong right now' },
            { id: 'schedule', label: 'Schedule', hint: 'What is due and what is overdue' },
          ] as const).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveView(tab.id)}
              title={tab.hint}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeView === tab.id
                  ? 'bg-warning/20 text-warning'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      {activeView === 'health' && !useMockData && (
        <div className="flex justify-end">
          <ControlCentreDailyChecksPdfExport
            from={getDateRange.startDate.toISOString()}
            to={getDateRange.endDate.toISOString()}
            disabled={periodLoading}
          />
        </div>
      )}
      {activeView === 'health' && !periodError && periodPreparing && (
        <p className="text-sm text-ring">{STORED_VIEW_PREPARING_MESSAGE}</p>
      )}
      {activeView === 'health' && !periodError && !periodPreparing && storedViewStaleMessage(summary?.projection) && (
        <p className="text-sm text-warning">{storedViewStaleMessage(summary?.projection)}</p>
      )}
      {activeView === 'health' && periodError && (
        <p className="text-sm text-destructive">{periodError}</p>
      )}
    </div>
  );

  // Schedule is not period-driven: outstanding work must always be visible.
  // Completions for the on-time KPI and calendar history use a fixed rolling
  // window aligned with Aquarius maintenance catch-up / outstanding lookback.
  const nowForSchedule = new Date();
  const todayStr = nowForSchedule.toISOString().split('T')[0];
  const scheduleFrom = new Date(nowForSchedule);
  scheduleFrom.setUTCDate(scheduleFrom.getUTCDate() - (MAINTENANCE_SCHEDULE_HISTORY_DAYS - 1));
  const scheduleBlock = (
    <MaintenanceScheduleView
      from={new Date(`${scheduleFrom.toISOString().split('T')[0]}T00:00:00.000Z`).toISOString()}
      to={new Date(`${todayStr}T23:59:59.999Z`).toISOString()}
      refreshKey={scheduleTruth.refreshKey}
      onTruthFetchStart={scheduleTruth.markTruthFetch}
    />
  );

  if (!data && !showLoading) {
    return (
      <div className="space-y-6">
        {headerBlock}
        {activeView === 'schedule' ? (
          scheduleBlock
        ) : (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Wrench className="w-12 h-12 text-warning/60" />
            <p className="text-muted-foreground text-center max-w-md">
              No maintenance data yet. Refresh Control Centre to ingest Samsara faults and Aquarius defects / VOR.
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 px-6 py-3 bg-warning hover:bg-warning text-white rounded-lg"
            >
              <RefreshCw className="w-5 h-5" />
              Load Maintenance Data
            </button>
          </div>
        )}
      </div>
    );
  }

  if (showLoading && !data) {
    return (
      <div className="space-y-6">
        {headerBlock}
        {activeView === 'schedule' ? (
          scheduleBlock
        ) : (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
            <RefreshCw className="w-8 h-8 animate-spin text-warning" />
            <p className="text-sm">Loading maintenance data…</p>
          </div>
        )}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      {headerBlock}

      {activeView === 'schedule' && scheduleBlock}

      {activeView === 'health' && (
      <>
      {!useMockData && (
        <DailySafetyBriefPanel
          from={briefRange.from}
          to={briefRange.to}
          scope="maintenance"
          onNavigate={handleBriefNavigate}
        />
      )}

      <div ref={kpiGridRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        <ControlCentreKPICard
          id="kpi-open-defects"
          className="h-full"
          title="Outstanding Defects"
          value={notFixedDefectCount}
          subtitle={`${inProgressDefectCount} in progress · across ${groupedDefects.length} vehicles`}
          icon={AlertTriangle}
          variant="primary"
          colorScheme="red"
          onClick={openDefectsModal}
        />
        <ControlCentreKPICard
          id="kpi-vor"
          className="h-full"
          title="Vehicles on VOR"
          value={data.vor_asset_count}
          subtitle="Currently off road"
          icon={CarFront}
          variant="primary"
          colorScheme={data.vor_asset_count > 0 ? 'red' : 'green'}
          onClick={() =>
            setKpiModal({
              open: true,
              title: 'Vehicles on VOR',
              subtitle: `${data.vor_asset_count} currently off road`,
              columns: VOR_COLUMNS,
              rows: sortByRecency(data.vor_assets || [], (v) => v.date_reported),
              searchKeys: ['vehicle', 'vor_start', 'reason', 'vor_category'],
            })
          }
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-card/50 border border-border rounded-xl p-4">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h4 className="text-base font-semibold text-white">Defects reported (last 7 days)</h4>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: DEFECT_NOT_FIXED_COLOR }} aria-hidden /> Not Fixed</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: DEFECT_IN_PROGRESS_COLOR }} aria-hidden /> In Progress</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: DEFECT_FIXED_COLOR }} aria-hidden /> Fixed</span>
            </div>
          </div>
          {/* Same chart as the Orders trend (BarChart): fonts, axis and spacing. */}
          {defectsByDayUnavailable ? (
            <p className="text-sm text-warning/90 py-6 w-full text-center">Defects reported for the last 7 days could not be read just now.</p>
          ) : (
            <BarChart
              data={defectChartDays.map((d) => ({
                label: `${d.label}\n(${d.day.slice(8, 10)}/${d.day.slice(5, 7)})`,
                values: [
                  { vehicle: 'Not Fixed', value: d.not_fixed, color: DEFECT_NOT_FIXED_COLOR, stack: 'open' },
                  { vehicle: 'In Progress', value: d.in_progress, color: DEFECT_IN_PROGRESS_COLOR, stack: 'open' },
                  { vehicle: 'Fixed', value: d.fixed, color: DEFECT_FIXED_COLOR },
                ],
              }))}
              height="h-56"
              compact
              embedded
              stacked
              hideLegend
              integerTicks
              onGroupClick={(i) => {
                const day = defectChartDays[i];
                if (day && day.count > 0) openDefectDay(day.day);
              }}
            />
          )}
        </div>

        <div className="lg:col-span-2 bg-card/50 border border-border rounded-xl p-5">
          <h4 className="text-base font-semibold text-white mb-4">
            Latest status by defect
          </h4>
          <div className="flex flex-col gap-3">
            {statusRows.length === 0 && (
              <p className="text-sm text-muted-foreground">No defect statuses to show</p>
            )}
            {statusRows.map((row) => (
              <div key={row.label} className="flex items-center gap-3">
                <span className="w-20 text-xs text-muted-foreground shrink-0">{row.label}</span>
                <div className="flex-1 h-2.5 bg-background/80 rounded overflow-hidden">
                  <div
                    className={`h-full rounded ${
                      row.tone === 'critical'
                        ? 'bg-destructive'
                        : row.tone === 'major'
                          ? 'bg-warning'
                          : 'bg-success'
                    }`}
                    style={{ width: `${Math.max(4, row.pct)}%` }}
                  />
                </div>
                <span className="w-6 text-right text-xs font-mono font-semibold text-foreground">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DataTable
        title={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between w-full">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <span>Defect status</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {(['all', 'open', 'Notfixed', 'InProgress', 'Fixed'] as const).map((key) => {
                const active = defectStatusFilter === key;
                const label =
                  key === 'open'
                    ? 'Open'
                    : key === 'all'
                      ? 'All'
                      : formatDefectStatusLabel(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDefectStatusFilter(key)}
                    className={`px-2 py-0.5 text-xs rounded border ${
                      active
                        ? 'bg-ring/30 border-ring text-ring'
                        : 'bg-card border-border text-muted-foreground hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        }
        hoverColor="red"
        disableRowHover
        hideExport
        maxRows={8}
        headerRightContent={
          <button
            type="button"
            className="text-xs text-ring hover:text-ring font-semibold"
            onClick={openDefectsModal}
          >
            View all {statusFilteredDefects.length} →
          </button>
        }
        customColumnWidths={{
          vehicle: { minWidth: 10, maxWidth: 12 },
          defect_count: { minWidth: 6, maxWidth: 8 },
        }}
        columns={[
          { key: 'vehicle', label: 'Vehicle Reg', align: 'left' },
          { key: 'defect_count', label: 'Defects', align: 'right' },
          { key: 'status', label: 'Status', align: 'left' },
          { key: 'latest_audit', label: 'Last Updated', align: 'left' },
          { key: 'vor', label: 'VOR', align: 'left' },
        ]}
        data={defectTableRows}
        onRowClick={(row) => setExpandedDefectVehicle((cur) => (cur === row.id ? null : String(row.id)))}
        expandedRowId={expandedDefectVehicle}
        renderExpandedRow={(row) => <VehicleDefectsDetail defects={row._group.defects} />}
      />

      <DataTable
        title={
          <div className="flex items-center gap-2">
            <CarFront className="w-5 h-5 text-destructive" />
            <span>Vehicles on VOR</span>
          </div>
        }
        hoverColor="red"
        disableRowHover
        hideExport
        maxRows={8}
        columns={[
          { key: 'vehicle', label: 'Vehicle Reg', align: 'left' },
          { key: 'vor_start', label: 'Start Date', align: 'left' },
          { key: 'vor_days', label: 'Days Off-Road', align: 'right' },
          { key: 'last_update', label: 'Last Updated', align: 'left' },
          { key: 'reason', label: 'Reason', align: 'left', wrap: true },
          { key: 'vor_category', label: 'Category', align: 'left', wrap: true },
        ]}
        customColumnWidths={{
          vehicle: { minWidth: 12, maxWidth: 16 },
          vor_start: { minWidth: 12, maxWidth: 14 },
          vor_days: { minWidth: 10, maxWidth: 14 },
          last_update: { minWidth: 12, maxWidth: 16 },
          reason: { minWidth: 32, maxWidth: 48 },
          vor_category: { minWidth: 14, maxWidth: 20 },
        }}
        data={vorTableRows}
      />
      </>
      )}

      <KPIDataModal
        isOpen={kpiModal.open}
        onClose={() => setKpiModal((s) => ({ ...s, open: false }))}
        title={kpiModal.title}
        subtitle={kpiModal.subtitle}
        columns={kpiModal.columns}
        data={kpiModal.rows}
        searchKeys={kpiModal.searchKeys}
      />
    </div>
  );
}
